import { AsyncPipe, CommonModule, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { TranslateModule } from '@ngx-translate/core';
import {
  DuplicateNameDirective,
  MatErrorMessagesDirective,
} from 'app/shared/directives';
import {
  ButtonColor,
  ButtonShape,
  ButtonStyle,
  ButtonType,
} from 'app/shared/enums';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  startWith,
} from 'rxjs';
import { ButtonComponent } from 'shared/components/atoms';

@Component({
    selector: 'app-searchable-input-list-editor',
    templateUrl: './searchable-input-list-editor.component.html',
    styles: ``,
    imports: [
        CommonModule,
        NgClass,
        NgIf,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatListModule,
        TranslateModule,
        AsyncPipe,
        ButtonComponent,
        ReactiveFormsModule,
        DuplicateNameDirective,
        MatErrorMessagesDirective,
    ]
})
export class SearchableInputListEditorComponent<
  T extends { id: string; duplicateNames?: string[] },
> implements OnChanges
{
  private readonly cdRef = inject(ChangeDetectorRef);

  protected readonly ButtonShape = ButtonShape;
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ButtonType = ButtonType;
  protected readonly ButtonColor = ButtonColor;

  @Input() public items: T[] = [];
  @Input() public filterKey: keyof T | null = null;
  @Input() public label = '';
  @Input() public disabled = false;

  public form: FormGroup = new FormGroup({});
  public filteredList$!: Observable<T[]>;
  private searchTerm$ = new BehaviorSubject<string>('');

  private itemsSubject$ = new BehaviorSubject<T[]>([]);
  public items$ = this.itemsSubject$.asObservable();

  @Output() public add = new EventEmitter<string>();
  @Output() public edit = new EventEmitter<{ id: string; value: string }>();
  @Output() public delete = new EventEmitter<string>();

  @ViewChildren('field') public fields!: QueryList<ElementRef>;

  public ngOnChanges(): void {
    this.setItems(this.items);

    this.filteredList$ = combineLatest([
      this.items$,
      this.searchTerm$.pipe(startWith('')),
    ]).pipe(
      map(([items, searchTerm]) => {
        return items.filter((item) => {
          if (this.filterKey && typeof item[this.filterKey] === 'string') {
            return (item[this.filterKey] as string)
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
          }
          return false;
        });
      })
    );
  }

  public setItems(items: T[]): void {
    this.itemsSubject$.next(items);

    items.forEach((item) => {
      if (!this.form.contains(item.id)) {
        this.form.addControl(
          item.id,
          new FormControl(item[this.filterKey as keyof T] || '', [
            Validators.required,
          ])
        );

        if (this.hasSavedItem(item.id)) {
          this.form.get(item.id)?.disable();
        }
      }
    });

    items.forEach((currentItem) => {
      currentItem.duplicateNames = items
        .filter((item) => item.id !== currentItem.id)
        .map((item) => this.form.get(item.id)?.value?.toLowerCase());
    });

    Object.keys(this.form.controls).forEach((controlName) => {
      if (!items.find((item) => item.id === controlName)) {
        this.form.removeControl(controlName);
      }
    });
  }

  public updateSearchTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm$.next(input?.value || '');
  }

  public hasSavedItem(id: string): boolean {
    return !!this.items.find((item) => item.id === id);
  }

  public addItem(): void {
    if (!this.items.length) {
      this.searchTerm$.next('');
    }
    const newItem: T = {
      id: `field_${this.itemsSubject$.value.length + 1}`,
      ...(this.filterKey ? { [this.filterKey]: '' } : {}),
    } as T;

    const updatedItems = [...this.itemsSubject$.value, newItem];
    this.setItems(updatedItems);

    this.cdRef.detectChanges();

    const fieldElements = this.fields.toArray();
    const newField = fieldElements[fieldElements.length - 1];
    if (newField) {
      newField.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  public deleteItem(id: string, cancel = false): void {
    if (this.hasSavedItem(id)) {
      const item = this.items.find((item) => item.id === id);
      this.form
        .get(id)
        ?.setValue((item && item[this.filterKey as keyof T]) || '');
      this.form.get(id)?.disable();
      this.form.get(id)?.markAsUntouched();
      this.form.get(id)?.markAsPristine();
      if (!cancel) {
        this.delete.emit(id);
      }
      return;
    }
    const updatedItems = this.itemsSubject$.value.filter(
      (item) => item.id !== id
    );
    this.setItems(updatedItems);
  }

  public editItem(id: string): void {
    this.form.get(id)?.enable();
    this.form.get(id)?.markAsTouched();
  }

  public emitConfirm(id: string): void {
    if (this.hasSavedItem(id)) {
      this.edit.emit({ id: id, value: this.form.get(id)?.value });
      this.form.get(id)?.disable();
      this.form.get(id)?.markAsUntouched();
      this.form.get(id)?.markAsPristine();
      return;
    }

    this.add.emit(this.form.get(id)?.value);
  }
}
